import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, GameIcon } from "../../components/game";

/**
 * Athlete-facing explainer for how points are earned. Reached from the
 * Leaderboard header ("Info puncte"). The numbers here are the platform
 * defaults — a club can adjust some of them, which is called out in the note.
 */
export default function AppPointsInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // One "source of points" section.
  const Section = ({
    icon,
    tint,
    title,
    children,
  }: {
    icon: React.ReactNode;
    tint: string;
    title: string;
    children: React.ReactNode;
  }) => (
    <GameCard className="p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${tint}`}>{icon}</div>
        <h2 className="font-extrabold text-slate-800">{title}</h2>
      </div>
      <div className="space-y-2.5 text-sm text-slate-600">{children}</div>
    </GameCard>
  );

  // A "+N puncte — label" row.
  const Row = ({ value, label, negative }: { value: string; label: string; negative?: boolean }) => (
    <div className="flex items-center gap-3">
      <span
        className={`inline-flex items-center justify-center min-w-[54px] px-2 py-1 rounded-lg text-xs font-extrabold tabular-nums ${
          negative ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
        }`}
      >
        {value}
      </span>
      <span className="flex-1">{label}</span>
    </div>
  );

  return (
    <MobileAppLayout>
      <div className="pb-6">
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <button
            onClick={() => navigate(-1)}
            className="-ml-1.5 mb-1.5 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label={t("common.back", "Înapoi")}
          >
            <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-extrabold">{t("pointsInfo.title", "Cum câștigi puncte")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">
            {t("pointsInfo.subtitle", "Tot ce trebuie să știi despre puncte")}
          </p>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
          {/* Intro */}
          <GameCard className="p-4">
            <p className="text-sm text-slate-600">
              {t(
                "pointsInfo.intro",
                "Punctele îți arată progresul, îți urcă nivelul, deblochează insigne și te clasează în topuri. Le câștigi din mai multe activități:",
              )}
            </p>
          </GameCard>

          {/* Quizzes */}
          <Section
            tint="bg-violet-100"
            title={t("pointsInfo.quizTitle", "Quiz-uri")}
            icon={<GameIcon name="bulb" className="w-5 h-5" />}
          >
            <p>
              {t(
                "pointsInfo.quizIntro",
                "Fiecare întrebare valorează un număr de puncte, în funcție de tip. Răspunsul corect îți aduce punctele întrebării:",
              )}
            </p>
            <Row value="+5" label={t("pointsInfo.quizTrueFalse", "Întrebare adevărat / fals")} />
            <Row value="+10" label={t("pointsInfo.quizSingle", "Întrebare cu un singur răspuns")} />
            <Row value="+15" label={t("pointsInfo.quizMulti", "Întrebare cu răspunsuri multiple")} />
            <p className="pt-1">
              <span className="font-semibold text-slate-700">{t("pointsInfo.quizSpeedTitle", "Bonus de viteză:")}</span>{" "}
              {t(
                "pointsInfo.quizSpeed",
                "dacă întrebarea are cronometru, cu cât răspunzi mai repede, cu atât primești mai multe puncte. Un răspuns lent aduce mai puține (minim ~20% din punctele întrebării).",
              )}
            </p>
            <Row value="0" label={t("pointsInfo.quizWrong", "Răspuns greșit — niciun punct")} />
            <Row value="−5" negative label={t("pointsInfo.quizRetake", "Reluarea unui quiz deja finalizat")} />
            <p className="text-xs text-slate-400 pt-1">
              {t("pointsInfo.quizNote", "Valorile pe întrebare pot fi diferite — le stabilește cel care creează quiz-ul.")}
            </p>
          </Section>

          {/* Attendance */}
          <Section
            tint="bg-emerald-100"
            title={t("pointsInfo.attendanceTitle", "Prezența la antrenamente")}
            icon={
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          >
            <p>
              {t(
                "pointsInfo.attendanceIntro",
                "Când antrenorul îți marchează prezența la o sesiune, primești puncte o singură dată pentru acea sesiune:",
              )}
            </p>
            <Row value="+10" label={t("pointsInfo.attendancePresent", "Prezent")} />
            <Row value="+5" label={t("pointsInfo.attendanceLate", "Întârziat")} />
            <Row value="0" label={t("pointsInfo.attendanceAbsent", "Absent sau motivat")} />
            <p className="text-xs text-slate-400 pt-1">
              {t("pointsInfo.attendanceNote", "Clubul tău poate ajusta aceste valori.")}
            </p>
          </Section>

          {/* Leaderboard prizes */}
          <Section
            tint="bg-amber-100"
            title={t("pointsInfo.leaderboardTitle", "Premii de clasament")}
            icon={<GameIcon name="trophy" className="w-5 h-5" />}
          >
            <p>
              {t(
                "pointsInfo.leaderboardIntro",
                "La finalul fiecărei săptămâni și luni, sportivii de pe primele locuri din clasament primesc puncte bonus de premiu. Cu cât ești mai sus, cu atât premiul e mai mare.",
              )}
            </p>
            <p className="text-xs text-slate-400">
              {t("pointsInfo.leaderboardNote", "Clasamentul se calculează pe puncte, la nivel de club și de platformă.")}
            </p>
          </Section>

          {/* Other */}
          <Section
            tint="bg-blue-100"
            title={t("pointsInfo.otherTitle", "Alte surse")}
            icon={<GameIcon name="star" className="w-5 h-5" />}
          >
            <div className="flex items-start gap-2.5">
              <GameIcon name="bolt" className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{t("pointsInfo.otherVideos", "Videoclipuri educative — primești puncte când te uiți la lecțiile video.")}</p>
            </div>
            <div className="flex items-start gap-2.5">
              <GameIcon name="medal" className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{t("pointsInfo.otherEvents", "Competiții și evenimente la care participi.")}</p>
            </div>
            <div className="flex items-start gap-2.5">
              <GameIcon name="star" className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{t("pointsInfo.otherBonus", "Bonusuri acordate de antrenor pentru efort deosebit.")}</p>
            </div>
          </Section>

          {/* What points do */}
          <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-4">
            <p className="text-sm text-indigo-800">
              <span className="font-bold">{t("pointsInfo.whyTitle", "La ce folosesc punctele?")}</span>{" "}
              {t(
                "pointsInfo.whyBody",
                "Punctele adunate îți cresc nivelul, deblochează insigne și îți stabilesc locul în clasament. Nu scad decât dacă reiei un quiz deja finalizat.",
              )}
            </p>
          </div>
        </div>
      </div>
    </MobileAppLayout>
  );
}
